import { useLanguage } from '../../context/LanguageContext';
import { getBadgeInfo, BADGES } from '../../services/legendApi';
import styles from './RiderBadge.module.css';

/**
 * 骑士徽章组件
 *
 * 展示骑士获得的成就徽章
 * 支持多种尺寸和展示模式
 */
export default function RiderBadge({
    badgeId,
    size = 'medium', // small | medium | large
    showTooltip = true,
    showName = false,
    className = '',
}) {
    const { language } = useLanguage();

    const badge = getBadgeInfo(badgeId, language);

    if (!badge) return null;

    const sizeClass = styles[size] || styles.medium;

    return (
        <div
            className={`${styles.badge} ${sizeClass} ${className}`}
            data-category={badge.category}
            title={showTooltip ? badge.name : undefined}
        >
            <span className={styles.icon}>{badge.icon}</span>
            {showName && (
                <span className={styles.name}>{badge.name}</span>
            )}
        </div>
    );
}

/**
 * 徽章墙组件 - 展示多个徽章
 */
export function BadgeWall({
    badges = [],
    maxDisplay = 5,
    size = 'medium',
    showNames = false,
    className = '',
}) {
    const { language } = useLanguage();
    const displayBadges = badges.slice(0, maxDisplay);
    const remaining = badges.length - maxDisplay;

    return (
        <div className={`${styles.badgeWall} ${className}`}>
            {displayBadges.map(badgeId => (
                <RiderBadge
                    key={badgeId}
                    badgeId={badgeId}
                    size={size}
                    showTooltip={true}
                    showName={showNames}
                />
            ))}
            {remaining > 0 && (
                <span className={styles.moreCount}>
                    +{remaining}
                </span>
            )}
        </div>
    );
}

/**
 * 全部徽章展示组件 - 用于展示所有可获得的徽章
 */
export function AllBadges({
    earnedBadges = [],
    showLocked = true,
    className = '',
}) {
    const { language } = useLanguage();

    const categoryLabels = {
        creation: { zh: '创作类', en: 'Creation', de: 'Kreativ' },
        social: { zh: '社交类', en: 'Social', de: 'Sozial' },
        honor: { zh: '荣誉类', en: 'Honor', de: 'Ehren' },
    };

    const categories = [
        { id: 'creation', title: categoryLabels.creation[language] || categoryLabels.creation.en },
        { id: 'social', title: categoryLabels.social[language] || categoryLabels.social.en },
        { id: 'honor', title: categoryLabels.honor[language] || categoryLabels.honor.en },
    ];

    return (
        <div className={`${styles.allBadges} ${className}`}>
            {categories.map(category => {
                const categoryBadges = Object.values(BADGES).filter(
                    b => b.category === category.id
                );

                return (
                    <div key={category.id} className={styles.badgeCategory}>
                        <h4 className={styles.categoryTitle}>{category.title}</h4>
                        <div className={styles.badgeGrid}>
                            {categoryBadges.map(badge => {
                                const isEarned = earnedBadges.includes(badge.id);
                                const badgeInfo = getBadgeInfo(badge.id, language);

                                if (!showLocked && !isEarned) return null;

                                return (
                                    <div
                                        key={badge.id}
                                        className={`${styles.badgeItem} ${isEarned ? styles.earned : styles.locked}`}
                                    >
                                        <span className={styles.badgeIcon}>
                                            {badgeInfo.icon}
                                        </span>
                                        <span className={styles.badgeName}>
                                            {badgeInfo.name}
                                        </span>
                                        {!isEarned && (
                                            <span className={styles.lockIcon}>🔒</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
